import React, {
  FunctionComponent,
  useMemo,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import InfinityScrollWrapper from 'Shared/components/InfinityScrollWrapper'
import Alert from 'Shared/components/Alert'
import {
  getFeedTracks,
  getReviewedFeedTracks,
  skipFeedReview,
} from 'Redux/actions/feedActions'
import {
  getAwaitingTracksSelector,
  getTotalAwaitingTracksSelector,
  getReviewedTracksSelector,
  getTotalReviewedTracksSelector,
  getIsLimitReached,
  getFeedDetails,
} from 'Redux/selectors/feed'
import { getUserId } from 'Redux/selectors/auth'
import {
  FeedFilterType,
  FeedFilterDirection,
  CampaignPlatformTypeEnum,
  FeedDetails,
} from 'Types/index'

import { reviewSkippedEvent } from 'Helpers/tagManager'

import { Modal } from '../Modal'
import { ReviewTrack } from '../ReviewTrack'
import { ReviewSentSuccess } from '../ReviewSentSuccess'
import SkipReviewModal from '../SkipReviewModal/SkipReviewModal'
import { ReviewedListItem } from '../ReviewedListItem'
import { TrackListItem } from '../TrackListItem'
import { ListFilter } from '../ListFilter'
import {
  TrackListProps,
  Tabs,
  SkipMobileProps,
  ReviewMobileProps,
  Review,
} from './props'
import {
  Container,
  TabTitles,
  TabPane,
  TabButtonTitle,
  TabButtonCount,
  StyledPagination,
  PaginationContainer,
  EmptyListContainer,
  EmptyListPlaceholder,
  ScrollTrigger,
  ListLimitMessage,
  AlertText,
  AlertTitle,
  AlertButton,
} from './styles'

export const TrackList: FunctionComponent<TrackListProps> = ({
  setVisibleContent,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const userId = useSelector(getUserId)
  const awaitingCampaigns = useSelector(getAwaitingTracksSelector)
  const reviewedCampaigns = useSelector(getReviewedTracksSelector)
  const isLimitReached = useSelector(getIsLimitReached)

  const totalFeed: number = useSelector(getTotalAwaitingTracksSelector)
  const totalReviewed: number = useSelector(getTotalReviewedTracksSelector)

  const [awaitingPaginator, setAwaitingPaginator] = useState<number>(1)
  const [reviewedPaginator, setReviewedPaginator] = useState<number>(1)
  const [modalHeaderGreen, setModalHeaderGreen] = useState<boolean>(true)
  const [currentTab, setCurrentTab] = useState<string>(Tabs.awaiting_review)

  const [isReviewModalVisible, setIsReviewModalVisible] = useState<boolean>(
    false
  )
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState<boolean>(
    false
  )
  const [skipReviewModalVisible, setSkipReviewModalVisible] = useState(false)
  const [skipModalData, setSkipModalData] = useState<SkipMobileProps>()
  const [reviewModalData, setReviewModalData] = useState<ReviewMobileProps>()
  const [isReviewedLoading, setIsReviewedLoading] = useState<boolean>(false)
  const [
    isActualCampaignDetailsLoaded,
    setIsActualCampaignDetailsLoaded,
  ] = useState(false)

  const [filterType, setFilterType] = useState<FeedFilterType>(
    FeedFilterType.date
  )
  const [filterDirection, setFilterDirection] = useState<FeedFilterDirection>(
    FeedFilterDirection.ASC
  )

  const getCurrentReviewedTracks = () => {
    setReviewedPaginator(prevValue => {
      setIsReviewedLoading(true)
      dispatch(
        getReviewedFeedTracks({
          page: prevValue + 1,
        })
        // @ts-ignore
      ).then(() => setIsReviewedLoading(false))
      return prevValue + 1
    })
  }

  const mobileHideContent = useMemo(
    () =>
      !!(
        window.innerWidth < 576 &&
        (isReviewModalVisible || isSuccessModalVisible)
      ),
    [isReviewModalVisible, isSuccessModalVisible]
  )

  const onActionCallback = () => {
    window.scrollTo(0, 0)
    dispatch(
      getFeedTracks({
        filterType,
        filterDirection,
      })
    )
    dispatch(
      getReviewedFeedTracks({
        page: 1,
      })
    )
  }

  const openReviewModal = (props: ReviewMobileProps) => {
    setReviewModalData(props)
    setIsReviewModalVisible(true)
    setVisibleContent(false)
  }

  const closeReviewModal = () => {
    setVisibleContent(true)
    setIsReviewModalVisible(false)
  }

  const openSkipModal = (props: SkipMobileProps) => {
    setIsReviewModalVisible(false)
    setSkipModalData(props)
    setSkipReviewModalVisible(true)
    setVisibleContent(false)
  }

  const skipReviewModalToggle = () => {
    setSkipReviewModalVisible(!skipReviewModalVisible)
    setVisibleContent(true)
  }
  const reviewDetails: FeedDetails = useSelector(getFeedDetails)

  const handleSkipReview = () => {
    dispatch(
      skipFeedReview({
        campaign_id: `${skipModalData?.campaign_id}`,
        callback: error => {
          if (!error) {
            skipReviewModalToggle()
          }
          onActionCallback()
        },
      })
    )
    skipModalData &&
      reviewSkippedEvent(
        skipModalData.campaign_id,
        new Date().getTime(),
        CampaignPlatformTypeEnum.SPOTIFY,
        0,
        userId,
        true
      )
  }

  const onSuccess = () => {
    setIsSuccessModalVisible(true)
    onActionCallback()
  }

  useEffect(() => {
    setReviewedPaginator(() => {
      dispatch(
        getReviewedFeedTracks({
          page: 1,
        })
        // @ts-ignore
      ).then(() => setIsReviewedLoading(false))
      return 1
    })
  }, [])

  useEffect(() => {
    onActionCallback()
  }, [filterDirection, filterType])

  useEffect(() => {
    if (isSuccessModalVisible) {
      onActionCallback()
    }
  }, [isSuccessModalVisible])

  const onCloseSuccess = () => {
    setVisibleContent(true)
    setIsReviewModalVisible(false)
    setIsSuccessModalVisible(false)
    setModalHeaderGreen(true)
  }

  const memoAwaitingCampaigns = useMemo(() => {
    if (awaitingCampaigns.length) {
      return (
        <>
          {awaitingCampaigns.map(el => {
            if (el.campaign) {
              return (
                <TrackListItem
                  key={el.campaign.id}
                  track={el}
                  openReviewModal={openReviewModal}
                  openSkipModal={openSkipModal}
                  onActionCallback={onActionCallback}
                />
              )
            }
            return null
          })}
        </>
      )
    }
    return (
      <EmptyListContainer>
        <EmptyListPlaceholder>
          {t('reviewsPage.emptyAwaitingList')}
        </EmptyListPlaceholder>
      </EmptyListContainer>
    )
  }, [awaitingCampaigns])

  const memoReviewedCampaigns = useMemo(() => {
    if (reviewedCampaigns.length) {
      return (
        <>
          {reviewedCampaigns.map(el => (
            <ReviewedListItem
              key={el.campaign.id}
              // @ts-ignore
              review={el}
              openReviewModal={openReviewModal}
              openSkipModal={openSkipModal}
              onActionCallback={onActionCallback}
            />
          ))}
        </>
      )
    }
    return (
      <EmptyListContainer>
        <EmptyListPlaceholder>
          {t('reviewsPage.emptyReviewedList')}
        </EmptyListPlaceholder>
      </EmptyListContainer>
    )
  }, [reviewedCampaigns])

  const renderTabCount = (title: string, count: number) => (
    <>
      <TabButtonTitle>{title}</TabButtonTitle>
      <TabButtonCount>{count}</TabButtonCount>
    </>
  )

  const modalTitleMemo = useMemo(() => {
    if (!modalHeaderGreen) {
      return t('reviewsPage.warning')
    }
    if (!reviewModalData?.isReviewed) {
      return t('reviewsPage.modalTitle')
    }
    return t('reviewsPage.modalDetailsTitle')
  }, [modalHeaderGreen, reviewModalData])

  return (
    <>
      {!mobileHideContent && (
        <>
          {isLimitReached && (
            <Alert color="warning" icon unclosable>
              <AlertText>
                <AlertTitle>You have reached reviews daily limit</AlertTitle>
                <div>
                  We have collected more tracks for you so that you can pick up
                  only those that are for your playlists most. The same tracks
                  that are available for you are available for other curators
                  too. Tracks are available until selected by a curator.
                </div>
              </AlertText>
            </Alert>
          )}
          <Container bottomPadding={currentTab === Tabs.reviewed}>
            <TabTitles onChange={setCurrentTab}>
              <TabPane
                tab={renderTabCount('Awaiting review', totalFeed)}
                key={Tabs.awaiting_review}
              >
                {memoAwaitingCampaigns}
                {!!awaitingCampaigns?.length && (
                  <ListLimitMessage>
                    Your list of tracks is updated each time you complete
                    review, skip or sort tracks
                  </ListLimitMessage>
                )}
              </TabPane>
              <TabPane
                tab={renderTabCount('Reviewed', totalReviewed)}
                key={Tabs.reviewed}
              >
                <InfinityScrollWrapper
                  dataLength={reviewedCampaigns.length}
                  isLoading={isReviewedLoading}
                  next={getCurrentReviewedTracks}
                  hasMore={totalReviewed > reviewedCampaigns.length}
                >
                  {memoReviewedCampaigns}
                </InfinityScrollWrapper>
              </TabPane>
            </TabTitles>
            <ListFilter
              setFilterType={setFilterType}
              setFilterDirection={setFilterDirection}
              filterType={filterType}
              filterDirection={filterDirection}
              handleRefresh={onActionCallback}
              isFilterVisible={currentTab === Tabs.awaiting_review}
            />
          </Container>
        </>
      )}
      {isReviewModalVisible && (
        <Modal
          title={modalTitleMemo}
          isModalVisible={isReviewModalVisible}
          isActualCampaignDetailsLoaded={isActualCampaignDetailsLoaded}
          modalHeaderGreen={modalHeaderGreen}
          closeModal={onCloseSuccess}
          setIsActualCampaignDetailsLoaded={setIsActualCampaignDetailsLoaded}
          onActionCallback={onActionCallback}
        >
          {!isSuccessModalVisible && reviewModalData && (
            <ReviewTrack
              campaign_id={reviewModalData.campaign_id}
              openSkipModal={openSkipModal}
              setModalHeaderGreen={setModalHeaderGreen}
              setIsActualCampaignDetailsLoaded={
                setIsActualCampaignDetailsLoaded
              }
              onSuccess={onSuccess}
              onClose={closeReviewModal}
            />
          )}
          {isSuccessModalVisible && (
            <ReviewSentSuccess onCloseSucccess={onCloseSuccess} />
          )}
        </Modal>
      )}
      <SkipReviewModal
        modalIsOpen={skipReviewModalVisible}
        toggle={skipReviewModalToggle}
        handleSkipReview={handleSkipReview}
      />
    </>
  )
}
